/**
 * HTML Parser untuk optimize heading tags dan semantic structure
 */

interface HeadingInfo {
  level: number;
  text: string;
  id: string;
}

/**
 * Extract headings dari HTML content
 */
export const extractHeadings = (html: string): HeadingInfo[] => {
  const headings: HeadingInfo[] = [];
  const parser = new DOMParser();
  
  try {
    const doc = parser.parseFromString(html, 'text/html');
    const headingElements = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
    
    headingElements.forEach((el, index) => {
      const level = parseInt(el.tagName[1]);
      const text = el.textContent || '';
      
      // Generate slug-based ID (same logic as BlogPostPage ref callback)
      const slug = text
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
      
      const id = el.id || slug || `heading-${level}-${index}`;
      
      headings.push({ level, text, id });
    });
  } catch (error) {
    console.error('Error parsing HTML:', error);
  }
  
  return headings;
};

/**
 * Sanitize dan optimize HTML content
 * - Add IDs ke headings untuk linking
 * - Ensure proper heading hierarchy
 * - Add semantic attributes
 */
export const optimizeHtmlContent = (html: string): string => {
  const parser = new DOMParser();
  
  try {
    const doc = parser.parseFromString(html, 'text/html');
    
    // Process all headings
    let headingCounter = { h1: 0, h2: 0, h3: 0, h4: 0, h5: 0, h6: 0 };
    doc.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((el, index) => {
      const tag = el.tagName.toLowerCase() as keyof typeof headingCounter;
      headingCounter[tag]++;
      
      // Add ID if missing
      if (!el.id) {
        const text = el.textContent || '';
        const slug = text
          .toLowerCase()
          .trim()
          .replace(/[^\w\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-');
        
        el.id = slug || `${tag}-${index}`;
      }
      
      // Add role for accessibility
      el.setAttribute('role', 'heading');
    });
    
    // Process images - add figure/figcaption for better semantics
    doc.querySelectorAll('img:not([role])').forEach((img, index) => {
      if (img.parentElement?.tagName.toLowerCase() !== 'figure') {
        const figure = doc.createElement('figure');
        const figcaption = doc.createElement('figcaption');
        figcaption.className = 'text-sm text-gray-600 dark:text-gray-400 mt-2 text-center';
        figcaption.textContent = img.getAttribute('alt') || `Figure ${index + 1}`;
        
        img.parentElement?.insertBefore(figure, img);
        figure.appendChild(img);
        figure.appendChild(figcaption);
      }
    });
    
    // Return optimized HTML
    return doc.body.innerHTML;
  } catch (error) {
    console.error('Error optimizing HTML:', error);
    return html;
  }
};

/**
 * Generate table of contents dari headings
 */
export const generateTableOfContents = (headings: HeadingInfo[]): HeadingInfo[] => {
  return headings
    .filter(h => h.level >= 2 && h.level <= 3) // Only H2 and H3
    .map(h => ({
      ...h,
      id: h.id || `heading-${h.level}`,
    }));
};

/**
 * Create anchor link dari heading text
 */
export const slugifyText = (text: string): string => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
};
